/**
 * Super Admin Tiers Page - 요금제 관리 페이지
 */

import { Trans, t } from '@lingui/macro';
import { Edit, Trash2, Check, X, Loader2, Save, ChevronLeft } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tier, TIERS_COL, formatBytes, formatCount, formatPrice, DEFAULT_TIERS } from '../../models/Tier';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { IconPlus } from '../components/FigmaIcons';

// Tiers 목록 페이지
export const SuperAdminTiersPage: React.FC = () => {
    const navigate = useNavigate();
    const [tiers, setTiers] = useState<Tier[]>([]);
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(false);

    useEffect(() => {
        loadTiers();
    }, []);

    const loadTiers = async () => {
        try {
            setLoading(true);
            const q = query(collection(Fire.store, TIERS_COL), orderBy('displayOrder'));
            const snapshot = await getDocs(q);
            const items = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Tier));
            setTiers(items);
        } catch (error) {
            console.error('Failed to load tiers:', error);
        } finally {
            setLoading(false);
        }
    };

    const initializeDefaultTiers = async () => {
        if (!confirm('기본 요금제를 생성하시겠습니까?')) return;

        try {
            setInitializing(true);
            for (const tier of DEFAULT_TIERS) {
                await setDoc(doc(Fire.store, TIERS_COL, tier.id), {
                    ...tier,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            }
            await loadTiers();
        } catch (error) {
            console.error('Failed to initialize tiers:', error);
            alert('요금제 초기화에 실패했습니다.');
        } finally {
            setInitializing(false);
        }
    };

    const deleteTier = async (tierId: string) => {
        if (!confirm(`${tierId} 요금제를 삭제하시겠습니까?`)) return;

        try {
            await deleteDoc(doc(Fire.store, TIERS_COL, tierId));
            await loadTiers();
        } catch (error) {
            console.error('Failed to delete tier:', error);
            alert('요금제 삭제에 실패했습니다.');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">요금제 관리</h1>
                    <p className="text-gray-500">구독 요금제를 관리합니다.</p>
                </div>
                <div className="flex gap-2">
                    {tiers.length === 0 && (
                        <Button
                            variant="outline"
                            onClick={initializeDefaultTiers}
                            disabled={initializing}
                        >
                            {initializing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            기본 요금제 생성
                        </Button>
                    )}
                    <Button onClick={() => navigate('/super-admin/tiers/new')}>
                        <IconPlus className="w-4 h-4 mr-2" />
                        새 요금제
                    </Button>
                </div>
            </div>

            {tiers.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <p className="text-gray-500 mb-4">등록된 요금제가 없습니다.</p>
                        <Button variant="outline" onClick={initializeDefaultTiers} disabled={initializing}>
                            {initializing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            기본 요금제 생성
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {tiers.map((tier) => (
                        <Card key={tier.id} className={!tier.isActive ? 'opacity-60' : ''}>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            {tier.name}
                                            {!tier.isActive && (
                                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                                    비활성
                                                </span>
                                            )}
                                        </CardTitle>
                                        <CardDescription>{tier.id}</CardDescription>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => navigate(`/super-admin/tiers/${tier.id}`)}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => deleteTier(tier.id)}
                                        >
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* 가격 */}
                                <div className="text-center py-2 bg-gray-50 rounded-lg">
                                    <div className="text-2xl font-bold">
                                        {formatPrice(tier.price.monthly, tier.price.currency)}
                                    </div>
                                    <div className="text-sm text-gray-500">/ 月</div>
                                </div>

                                {/* 제한 사항 */}
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">ストレージ</span>
                                        <span>{formatBytes(tier.limits.storageBytes)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">ダウンロード/月</span>
                                        <span>{formatBytes(tier.limits.monthlyDownloadBytes)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">メンバー数</span>
                                        <span>{formatCount(tier.limits.memberCount)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">チャンネル数</span>
                                        <span>{formatCount(tier.limits.channelCount)}</span>
                                    </div>
                                </div>

                                {/* 기능 */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t">
                                    {tier.features.apiAccess && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                            API
                                        </span>
                                    )}
                                    {tier.features.analytics && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                            分析
                                        </span>
                                    )}
                                    {tier.features.customBranding && (
                                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                            ブランディング
                                        </span>
                                    )}
                                    {tier.features.prioritySupport && (
                                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                            優先サポート
                                        </span>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SuperAdminTiersPage;
